import type { FormHTMLAttributes, InputHTMLAttributes } from "react";

type PasswordManagerIgnoreProps = {
  "data-1p-ignore": string;
  "data-credential-purpose": string;
  "data-bwignore": string;
  "data-form-type": string;
  "data-lpignore": string;
  "data-nervedx-non-auth-field"?: string;
  "data-protonpass-ignore": string;
};

type NonAuthInputProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  | "id"
  | "autoComplete"
  | "name"
  | "autoCapitalize"
  | "autoCorrect"
  | "spellCheck"
> &
  PasswordManagerIgnoreProps;

type NonAuthFormProps = Pick<
  FormHTMLAttributes<HTMLFormElement>,
  "autoComplete"
> &
  PasswordManagerIgnoreProps;

export type NonAuthCredentialKind =
  "client-secret" | "api-key" | "token" | "password-like";

/**
 * Shared non-auth password-manager hardening helpers.
 *
 * Use these on admin/config/search/setup forms that can look credential-like
 * but are not account sign-in or account password-change flows.
 *
 * Example:
 * <Form {...getNonAuthFormProps("ehr-connection")}>
 *   <Form.Control {...getNonAuthCredentialInputProps("ehr-client-secret", "client-secret")} />
 * </Form>
 *
 * Real auth forms should keep browser-friendly values instead:
 * autocomplete="username", "current-password", "new-password", or
 * "one-time-code" as appropriate.
 */
function sanitizeFieldPurpose(purpose: string): string {
  const sanitized = purpose.replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
  return sanitized.replace(/-+/g, "-").replace(/^-|-$/g, "") || "field";
}

export function getNonAuthFieldId(purpose: string): string {
  return `non-auth-field-${sanitizeFieldPurpose(purpose)}`;
}

function getPasswordManagerIgnoreProps(): PasswordManagerIgnoreProps {
  return {
    "data-1p-ignore": "true",
    "data-credential-purpose": "non-auth-secret",
    "data-bwignore": "true",
    "data-form-type": "other",
    "data-lpignore": "true",
    "data-nervedx-non-auth-field": "true",
    "data-protonpass-ignore": "true",
  };
}

function getBaseNonAuthInputProps(
  purpose: string,
  role: string,
  autoComplete: NonAuthInputProps["autoComplete"],
): NonAuthInputProps {
  return {
    id: getNonAuthFieldId(`${role}-${purpose}`),
    name: `non-auth-${role}-${sanitizeFieldPurpose(purpose)}`,
    autoComplete,
    autoCapitalize: "none",
    autoCorrect: "off",
    spellCheck: false,
    ...getPasswordManagerIgnoreProps(),
  };
}

export function getNonAuthFormProps(purpose: string): NonAuthFormProps {
  return {
    autoComplete: "off",
    ...getPasswordManagerIgnoreProps(),
    "data-nervedx-non-auth-field": `form-${sanitizeFieldPurpose(purpose)}`,
  };
}

export function getNonAuthSearchInputProps(purpose: string): NonAuthInputProps {
  return getBaseNonAuthInputProps(purpose, "search", "off");
}

export function getNonAuthTextInputProps(purpose: string): NonAuthInputProps {
  return getBaseNonAuthInputProps(purpose, "text", "off");
}

export function getNonAuthEmailInputProps(purpose: string): NonAuthInputProps {
  return getBaseNonAuthInputProps(purpose, "email", "off");
}

export function getNonAuthSecretInputProps(purpose: string): NonAuthInputProps {
  return getBaseNonAuthInputProps(purpose, "secret", "new-password");
}

export function getNonAuthCredentialInputProps(
  purpose: string,
  kind: NonAuthCredentialKind,
): NonAuthInputProps {
  return getBaseNonAuthInputProps(
    purpose,
    `credential-${kind}`,
    "new-password",
  );
}

export function getNonAuthPasswordInputProps(
  purpose: string,
): NonAuthInputProps {
  return getNonAuthSecretInputProps(purpose);
}

export function getNonAuthIdentityInputProps(
  purpose: string,
): NonAuthInputProps {
  return getBaseNonAuthInputProps(purpose, "identity", "off");
}

export function getPasswordManagerIgnoreFormProps(): NonAuthFormProps {
  return getNonAuthFormProps("generic");
}
